import React, { useState } from "react";
import StructuralBackButton from "./shared/StructuralBackButton";
import AuthorityAssignmentField from "./shared/AuthorityAssignmentField";
import { Sparkline } from "./analytics/_utils";
import { Avatar } from "./shared/Avatar";

// =============================================================
// ComponentPreviewPage (/dev/components) — visual gallery used by the
// dev team to eyeball the shared UI primitives in one place. No data
// fetching; everything renders from local fixtures so it's safe to view
// in any environment. Linked from the future Storybook replacement.
// =============================================================
const SAMPLE_USERS = [
  { id: "u-pastor",  name: "Pastor Sample",   role: "pastor" },
  { id: "u-team-a",  name: "Sister Esther",   role: "team_member" },
  { id: "u-team-b",  name: "Brother Daniel",  role: "team_member" },
];

const SAMPLE_TREND = [12, 8, 16, 4, 20, 25, 22, 19, 28, 30, 26, 32, 35, 38];

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} style={{ padding: "20px 24px", background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, marginBottom: 20 }}>
      <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>{title}</h3>
      {children}
    </section>
  );
}

export default function ComponentPreviewPage() {
  const [authority, setAuthority] = useState(["u-pastor"]);
  return (
    <div data-testid="component-preview-page" style={{ padding: "24px clamp(16px,4vw,40px)", maxWidth: 960, margin: "0 auto", background: "var(--kc-bg)", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <StructuralBackButton to="/dashboard" label="← Dashboard" testId="dev-components-back" />
        <div>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: 22, color: "var(--kc-text)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, margin: 0 }}>Component Gallery</h1>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4 }}>Visual reference for shared UI primitives. Dev-only.</div>
        </div>
      </div>

      <Section title="Buttons" testId="dev-section-buttons">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="btn btn-gold">Primary Gold</button>
          <button className="btn btn-primary">Primary</button>
          <button className="btn btn-outline">Outline</button>
          <button className="btn btn-danger">Danger</button>
          <button className="btn btn-gold btn-sm">Small Gold</button>
          <button className="btn btn-outline btn-sm">Small Outline</button>
        </div>
      </Section>

      <Section title="Badges" testId="dev-section-badges">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <span className="badge badge-gold">Gold</span>
          <span className="badge badge-green">Green</span>
          <span className="badge badge-blue">Blue</span>
          <span className="badge badge-gray">Gray</span>
          <span className="badge badge-charcoal">Charcoal</span>
        </div>
      </Section>

      <Section title="Avatar" testId="dev-section-avatar">
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <Avatar name="Pastor Emmanuel" style={{ fontSize: 12 }} />
          <Avatar name="Sister Esther" style={{ fontSize: 12 }} />
          <Avatar name="Brother Daniel" style={{ fontSize: 12 }} />
          <Avatar name="Anonymous Visitor" style={{ width: 48, height: 48, fontSize: 14 }} />
        </div>
      </Section>

      <Section title="Sparkline (analytics trend)" testId="dev-section-sparkline">
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
          <Sparkline data={SAMPLE_TREND} testId="dev-sparkline-default" />
          <Sparkline data={SAMPLE_TREND} width={220} height={50} color="#A8D5BA" testId="dev-sparkline-large" />
          <Sparkline data={[0, 0, 0, 0]} testId="dev-sparkline-flat" />
        </div>
      </Section>

      <Section title="Authority Assignment field" testId="dev-section-authority">
        <AuthorityAssignmentField
          value={authority}
          onChange={setAuthority}
          users={SAMPLE_USERS}
          canEdit={true}
          testIdPrefix="dev-authority"
        />
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--kc-sub)" }}>Selected: {authority.length === 0 ? "(none)" : authority.join(", ")}</div>
      </Section>

      <Section title="Structural Back Button" testId="dev-section-back">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <StructuralBackButton to="/dashboard" />
          <StructuralBackButton to="/members" label="← All Members" />
          <StructuralBackButton to="/reports/analytics" label="← Analytics" />
        </div>
      </Section>

      <Section title="Card layouts" testId="dev-section-cards">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 14 }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="card" style={{ padding: 16 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Card #{i}</div>
              <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>Sample card content for spacing & contrast checks.</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
